import { CurrencyValue, formatNumber, Chart } from '@blockanalitica/ui';
import { ChartSkeleton } from '@/components';
import { ILKS_PALETTE } from '@/constants';
import { useMemo, useCallback } from 'react';
import { useApi } from '@/api';
import {
  ChartConfig,
  ChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { generateGradientColor } from '@/utils';

export interface IlkData {
  [cdp: string]: { ilk: string; value: string | number }[];
}

export default function DebtAtRiskGraph() {
  const [data, error] = useApi<IlkData>({
    url: '/at-risk/debt/'
  });

  if (error) {
    throw error;
  }

  const uniqueIlks = useMemo(() => new Set(), []);
  const chartDatasets: ChartDataset[] = useMemo(() => {
    if (!data) return [];

    const ilkCdpCount = {};
    const ilkCurrentIndex = {};

    Object.values(data).forEach((rows) => {
      const ilk = rows[0].ilk;
      ilkCdpCount[ilk] = (ilkCdpCount[ilk] || 0) + 1;
    });

    return Object.entries(data).flatMap(([key, rows]) => {
      const ilk = rows[0].ilk;
      uniqueIlks.add(ilk);
      ilkCurrentIndex[ilk] = ilkCurrentIndex[ilk] || 0;

      const cdpIndex = ilkCurrentIndex[ilk];
      const totalCdps = ilkCdpCount[ilk];

      const gradientColor = generateGradientColor(
        ILKS_PALETTE[ilk],
        cdpIndex,
        totalCdps
      );

      ilkCurrentIndex[ilk] += 1;

      return rows.map((row) => ({
        label: key,
        data: [
          {
            x: row.ilk,
            y: row.value
          }
        ],
        backgroundColor: gradientColor,
        borderColor: gradientColor
      }));
    });
  }, [data, uniqueIlks]);

  const cfg: ChartConfig = useMemo(() => {
    return {
      type: 'bar',
      options: {
        scales: {
          x: {
            stacked: true,
            type: 'category'
          },
          y: {
            stacked: true
          }
        },
        elements: {
          bar: {
            hoverBorderWidth: 0
          }
        }
      }
    };
  }, []);

  const xScaleValueFormatter = useCallback(
    (index: string | number): string => {
      const ilkList = Array.from(uniqueIlks);
      return String(ilkList[index] || '');
    },
    [uniqueIlks]
  );

  const valueFormatter = useCallback(
    (value: number) => (
      <CurrencyValue value={value} options={{ currency: 'USD' }} />
    ),
    []
  );

  const subValueFormatter = useCallback((value: string) => value, []);

  if (data && Object.keys(data).length > 0) {
    return (
      <Chart
        chartDatasets={chartDatasets}
        config={cfg}
        xScaleValueFormatter={xScaleValueFormatter}
        valueFormatter={valueFormatter}
        subValueFormatter={subValueFormatter}
        tooltip={{
          labelFormatter: (value) => `${value}`,
          titleFormatter: (value) => `Ilk: ${value}`,
          valueFormatter: (value) => `$${formatNumber(Number(value))}`
        }}
        showKpisAsTooltip
      />
    );
  }

  return <ChartSkeleton />;
}
